// Searchable multi-select facet dropdown for the SKU palette (dark t2 tokens).
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check, X, Search } from "lucide-react";

export default function FacetMultiSelect({ label, options, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const sel = useMemo(() => new Set(selected), [selected]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.name.toLowerCase().includes(s)) : options;
  }, [options, q]);
  const toggle = (v) => onChange(sel.has(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const active = selected.length > 0;

  return (
    <div ref={ref} style={{ position: "relative" }} data-testid={`facet-${testid}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`facet-${testid}-btn`}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 10px", borderRadius: 8, cursor: "pointer", fontSize: 11.5, fontWeight: 600,
          border: `1px solid ${active ? "var(--t2-orange)" : "var(--t2-border)"}`, background: active ? "rgba(255,104,32,0.12)" : "var(--t2-bg)",
          color: active ? "var(--t2-orange-ink)" : "var(--t2-grey1)", transition: "border-color .15s, background-color .15s" }}>
        <span className="t2-mono" style={{ fontSize: 9, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--t2-brass-ink)" }}>{label}</span>
        {active ? (selected.length === 1 ? selected[0] : `${selected.length} selected`) : "All"}
        {active
          ? <X className="w-3 h-3" onClick={(e) => { e.stopPropagation(); onChange([]); }} data-testid={`facet-${testid}-clear`} />
          : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div data-testid={`facet-${testid}-menu`}
          style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20, width: 260, background: "#0B1426", border: "1px solid var(--t2-border)", borderRadius: 10, boxShadow: "0 16px 40px rgba(0,0,0,.55)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", borderBottom: "1px solid var(--t2-border)" }}>
            <Search className="w-3.5 h-3.5" style={{ color: "var(--t2-grey3)" }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} data-testid={`facet-${testid}-search`}
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "var(--t2-white)", fontSize: 12 }} />
          </div>
          <div className="t2-scroll" style={{ maxHeight: 260, overflowY: "auto", padding: 4 }}>
            {list.length === 0 && <div style={{ padding: 10, fontSize: 11.5, color: "var(--t2-grey3)" }}>No matches</div>}
            {list.map((o) => {
              const on = sel.has(o.name);
              return (
                <button key={o.name} type="button" onClick={() => toggle(o.name)} data-testid={`facet-${testid}-opt-${o.name.toLowerCase().replace(/\s+/g, "-")}`}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "6px 8px", borderRadius: 6, border: "none", cursor: "pointer", textAlign: "left", fontSize: 12,
                    background: on ? "rgba(255,104,32,0.12)" : "transparent", color: on ? "var(--t2-white)" : "var(--t2-grey1)" }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
                    border: `1px solid ${on ? "var(--t2-orange)" : "var(--t2-grey4)"}`, background: on ? "var(--t2-orange)" : "transparent" }}>
                    {on && <Check className="w-2.5 h-2.5" style={{ color: "#0b0f17" }} />}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name}</span>
                  <span className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)" }}>{o.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
